import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Apple, Chrome, Mail, Lock, User, AlertTriangle, X, Phone, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { teams as wcTeams } from '@/data/wc/groups';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@/components/ui/Card';
import { useAuthStore } from '@/stores/authStore';
import { buildAuthLink } from '@/lib/auth-attribution';
import {
  captureAttributionFromUrl,
  getAttributionSnapshotForUrl,
  trackFunnelEvent,
} from '@/lib/wc-funnel';
import {
  ACCOUNT_TERMS_CONSENT_TEXT,
  EMAIL_UPDATES_CONSENT_TEXT,
  SMS_CONSENT_TEXT,
} from '@/lib/privacy-consent';
import {
  isSocialAuthEnabled,
  SOCIAL_AUTH_LABELS,
  type SocialAuthProvider,
} from '@/lib/social-auth';
import AuthLegalLinks from '@/components/privacy/AuthLegalLinks';

const SOCIAL_PROVIDERS: Array<{
  provider: SocialAuthProvider;
  label: string;
  icon: React.ReactNode;
}> = [
  { provider: 'google', label: 'Continue with Google', icon: <Chrome className="h-4 w-4" /> },
  { provider: 'apple', label: 'Continue with Apple', icon: <Apple className="h-4 w-4" /> },
];

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = React.useState({
    email: '',
    displayName: '',
    phone: '',
    favoriteTeamId: '',
    wcUpdatesOptIn: false,
    smsOptIn: false,
    ageConfirmed: false,
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = React.useState(false);
  const [socialLoading, setSocialLoading] = React.useState<SocialAuthProvider | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [registrationSuccess, setRegistrationSuccess] = React.useState(false);
  const [errorRef, setErrorRef] = React.useState<string | null>(null);

  const { register, loginWithOAuth } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isWCBlitzHost = hostname === 'wcblitz.com' || hostname.endsWith('.wcblitz.com');
  const defaultRedirect = isWCBlitzHost ? '/wc/knockout' : '/wc';
  const redirectTo = searchParams.get('redirect') || defaultRedirect;
  const isWCSignup =
    redirectTo.startsWith('/wc') ||
    !!(searchParams.get('source') || searchParams.get('utm_source') || searchParams.get('ref'));
  const loginLink = buildAuthLink('/login', searchParams, redirectTo);
  const enabledSocialProviders = SOCIAL_PROVIDERS.filter(({ provider }) =>
    isSocialAuthEnabled(provider)
  );
  const favoriteTeamOptions = React.useMemo(
    () => [...wcTeams].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );
  const selectedFavoriteTeam = React.useMemo(
    () => favoriteTeamOptions.find(team => String(team.id) === formData.favoriteTeamId),
    [favoriteTeamOptions, formData.favoriteTeamId]
  );

  React.useEffect(() => {
    captureAttributionFromUrl(window.location.href);
    void trackFunnelEvent('signup_viewed', {
      surface: isWCSignup ? 'wc' : 'app',
      redirect_to: redirectTo,
    });
  }, [isWCSignup, redirectTo]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Display name validation
    if (!formData.displayName) {
      newErrors.displayName = 'Display name is required';
    } else if (formData.displayName.length < 2) {
      newErrors.displayName = 'Display name must be at least 2 characters';
    } else if (formData.displayName.length > 50) {
      newErrors.displayName = 'Display name must be less than 50 characters';
    }

    // Optional contact validation
    const normalizedPhone = formData.phone.replace(/[^\d+]/g, '');
    if (formData.phone && normalizedPhone.length < 7) {
      newErrors.phone = 'Please enter a valid phone number or leave it blank';
    }

    if (formData.smsOptIn && !formData.phone) {
      newErrors.phone = 'Add a phone number to get text alerts';
    }

    if (!formData.ageConfirmed) {
      newErrors.ageConfirmed = 'You must confirm you are at least 13 and agree to the terms and applicable rules';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const attribution = getAttributionSnapshotForUrl(window.location.href);
      const consentCapturedAt = new Date().toISOString();
      void trackFunnelEvent('signup_submitted', {
        surface: isWCSignup ? 'wc' : 'app',
        redirect_to: redirectTo,
      });

      const result = await register(formData.email, formData.password, formData.displayName, {
        attribution,
        redirectTo,
        capture: isWCSignup
          ? {
              phone: formData.phone.trim() || undefined,
              favoriteTeamId: selectedFavoriteTeam ? String(selectedFavoriteTeam.id) : undefined,
              favoriteTeamName: selectedFavoriteTeam?.name,
              favoriteTeamCode: selectedFavoriteTeam?.code,
              wcUpdatesOptIn: formData.wcUpdatesOptIn,
              smsOptIn: formData.smsOptIn && !!formData.phone.trim(),
              ageConfirmed: formData.ageConfirmed,
              consentCapturedAt,
              consentSourceUrl: window.location.href,
            }
          : {
              ageConfirmed: formData.ageConfirmed,
              consentCapturedAt,
              consentSourceUrl: window.location.href,
            },
      });

      void trackFunnelEvent(
        result.confirmationRequired ? 'signup_confirmation_required' : 'signup_created',
        {
          surface: isWCSignup ? 'wc' : 'app',
          redirect_to: redirectTo,
        }
      );

      if (result.confirmationRequired) {
        setRegistrationSuccess(true);
      } else {
        navigate(redirectTo, { replace: true });
      }
      setErrorRef(null);
    } catch (err: any) {
      // Generate support reference: BB-XXXXXX (timestamp-based, easy to search in logs)
      const ref = `BB-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      setErrorRef(ref);
      console.error(`[${ref}] Registration error:`, err);
      void trackFunnelEvent('signup_failed', {
        surface: isWCSignup ? 'wc' : 'app',
        redirect_to: redirectTo,
        reason: err instanceof Error ? err.message : 'unknown',
      });

      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Failed to create account';
      const msg = message.toLowerCase();

      // User-fixable errors (no ref needed, clear guidance)
      if (message === 'ACCOUNT_EXISTS') {
        setErrors({
          email: 'An account with this email already exists.',
          general:
            'Already have an account? Try signing in instead, or check your email for a confirmation link.',
        });
        setErrorRef(null); // user-fixable, no ref needed
      } else if (msg.includes('already registered') || msg.includes('already been registered')) {
        setErrors({
          email: 'This email is already registered.',
          general: 'Try signing in instead, or check your inbox for a verification email.',
        });
        setErrorRef(null);
      } else if (
        msg.includes('display name') &&
        (msg.includes('taken') ||
          msg.includes('exists') ||
          msg.includes('unique') ||
          msg.includes('duplicate'))
      ) {
        setErrors({ displayName: 'That display name is already taken. Try another one!' });
        setErrorRef(null);
      } else if (msg.includes('password')) {
        setErrors({ password: message });
        setErrorRef(null);
        // System/infra errors (show ref for support)
      } else if (
        msg.includes('rate') ||
        msg.includes('limit') ||
        msg.includes('too many') ||
        msg.includes('exceeded') ||
        msg.includes('temporarily unavailable')
      ) {
        setErrors({
          general: 'Our signup service is temporarily busy. Please wait 2–3 minutes and try again.',
        });
      } else if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout')) {
        setErrors({ general: 'Connection issue. Please check your internet and try again.' });
      } else if (msg.includes('email')) {
        setErrors({ email: message });
        setErrorRef(null);
      } else {
        setErrors({ general: 'Something went wrong creating your account. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignup = async (provider: SocialAuthProvider) => {
    setErrors({});
    setErrorRef(null);

    if (!formData.ageConfirmed) {
      setErrors({
        ageConfirmed: 'Confirm you are at least 13 and agree to the terms and applicable rules before continuing',
      });
      return;
    }

    setSocialLoading(provider);

    try {
      void trackFunnelEvent('signup_submitted', {
        surface: isWCSignup ? 'wc' : 'app',
        redirect_to: redirectTo,
        provider,
        method: 'oauth',
      });

      await loginWithOAuth(provider, {
        attribution: getAttributionSnapshotForUrl(window.location.href),
        redirectTo,
        mode: 'signup',
        ageConfirmed: formData.ageConfirmed,
      });
    } catch (err: any) {
      const ref = `BB-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      const message = err instanceof Error ? err.message : 'Failed to create account';
      const msg = message.toLowerCase();
      const providerName = SOCIAL_AUTH_LABELS[provider];
      console.error(`[${ref}] ${providerName} signup error:`, err);

      if (msg.includes('provider') && msg.includes('enabled')) {
        setErrors({
          general: `${providerName} signup is not configured yet. Please use email for now.`,
        });
        setErrorRef(null);
      } else if (msg.includes('popup') || msg.includes('cancel')) {
        setErrors({ general: `${providerName} signup was canceled. Please try again.` });
        setErrorRef(null);
      } else {
        setErrors({
          general: `Something went wrong creating your account with ${providerName}. Please try again.`,
        });
        setErrorRef(ref);
      }
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <main className="min-h-screen bg-dark-700 flex items-center justify-center px-4 py-12">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl animate-pulse-fire" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-fire delay-700" />
      </div>

      <div className="relative w-full max-w-md">
        {isWCSignup && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-center">
            <p className="text-sm font-semibold text-emerald-400">Free Sam's Army Knockout Challenge</p>
            <p className="mt-1 text-[10px] text-gray-400">
              Create an account so your bracket counts on the leaderboard and we can send your
              tournament updates if you opt in.
            </p>
          </div>
        )}

        <Card className="glass">
          <CardHeader className="text-center">
            {/* Logo */}
            <div className="mx-auto mb-4">
              <Link to="/" className="flex items-center justify-center space-x-2">
                <div className="h-12 w-12 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
                  <span className="text-white font-heading font-bold text-2xl">B</span>
                </div>
              </Link>
            </div>

            <CardTitle className="text-2xl font-heading text-gradient-fire">
              {isWCSignup ? 'Join World Cup Blitz' : 'Join BracketBlitz'}
            </CardTitle>
            <CardDescription>
              {isWCSignup
                ? "Save your bracket, beat Sam's Army, and get leaderboard updates."
                : 'Create your account and start building championship brackets today.'}
            </CardDescription>
          </CardHeader>

          <CardBody>
            {registrationSuccess ? (
              <div className="text-center py-6 space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Mail className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="text-xl font-heading text-text-primary">Check your email!</h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  We sent a confirmation link to{' '}
                  <span className="text-primary-400 font-medium">{formData.email}</span>.
                  <br />
                  Click the link to activate your account and start building brackets.
                </p>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 text-left">
                  <p className="text-amber-300 text-sm font-medium">📬 Don't see it?</p>
                  <p className="text-amber-200/70 text-xs mt-1">
                    Check your <strong>spam</strong> or <strong>promotions</strong> folder. The
                    email comes from Supabase and sometimes gets filtered.
                  </p>
                </div>
                <p className="text-text-muted text-xs">
                  Still nothing?{' '}
                  <button
                    onClick={() => setRegistrationSuccess(false)}
                    className="text-primary-400 hover:text-primary-300 underline"
                  >
                    Try signing up again
                  </button>{' '}
                  or{' '}
                  <Link
                    to={loginLink}
                    className="text-primary-400 hover:text-primary-300 underline"
                  >
                    sign in if you already verified
                  </Link>
                </p>
              </div>
            ) : (
              <>
                {enabledSocialProviders.length > 0 && (
                  <div className="mb-6 space-y-3">
                    {enabledSocialProviders.map(({ provider, label, icon }) => (
                      <Button
                        key={provider}
                        type="button"
                        variant="outline"
                        className="w-full border-slate-600 bg-slate-900/40 text-white hover:bg-slate-800"
                        size="lg"
                        leftIcon={icon}
                        loading={socialLoading === provider}
                        disabled={loading || !!socialLoading}
                        onClick={() => void handleSocialSignup(provider)}
                      >
                        {label}
                      </Button>
                    ))}
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
                      <div className="h-px flex-1 bg-slate-700" />
                      <span>or</span>
                      <div className="h-px flex-1 bg-slate-700" />
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email */}
                  <Input
                    type="email"
                    label="Email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    leftIcon={<Mail className="h-4 w-4" />}
                    autoComplete="email"
                    error={errors.email}
                    required
                    disabled={loading}
                  />

                  {/* Display Name */}
                  <Input
                    type="text"
                    label="Display Name"
                    placeholder="Choose a display name"
                    value={formData.displayName}
                    onChange={handleChange('displayName')}
                    leftIcon={<User className="h-4 w-4" />}
                    autoComplete="nickname"
                    error={errors.displayName}
                    required
                    disabled={loading}
                  />

                  {/* Password */}
                  <Input
                    type="password"
                    label="Password"
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={handleChange('password')}
                    leftIcon={<Lock className="h-4 w-4" />}
                    autoComplete="new-password"
                    error={errors.password}
                    required
                    disabled={loading}
                  />

                  {/* Confirm Password */}
                  <Input
                    type="password"
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    leftIcon={<Lock className="h-4 w-4" />}
                    autoComplete="new-password"
                    error={errors.confirmPassword}
                    required
                    disabled={loading}
                  />

                  {isWCSignup && (
                    <div className="space-y-4 rounded-lg border border-emerald-500/15 bg-emerald-500/5 p-4">
                      <div>
                        <p className="text-sm font-semibold text-emerald-300">
                          Optional fan details
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          Get more relevant leaderboard alerts, upset reminders, and future prize
                          updates.
                        </p>
                      </div>

                      <Input
                        type="tel"
                        label="Phone (optional)"
                        placeholder="Optional text alerts"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        leftIcon={<Phone className="h-4 w-4" />}
                        autoComplete="tel"
                        error={errors.phone}
                        disabled={loading}
                      />

                      <div className="space-y-2">
                        <label
                          htmlFor="favoriteTeamId"
                          className="block text-sm font-medium text-white"
                        >
                          Favorite team (optional)
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Trophy className="h-4 w-4 text-slate-400" />
                          </div>
                          <select
                            id="favoriteTeamId"
                            value={formData.favoriteTeamId}
                            onChange={e =>
                              setFormData(prev => ({
                                ...prev,
                                favoriteTeamId: e.target.value,
                              }))
                            }
                            disabled={loading}
                            className="input w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 pl-10 text-sm text-white transition-colors focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <option value="">Pick a team</option>
                            {favoriteTeamOptions.map(team => (
                              <option key={team.id} value={team.id}>
                                {team.name} ({team.code})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <label className="flex gap-3 rounded-lg border border-slate-700/70 bg-slate-900/35 p-3 text-left">
                        <input
                          type="checkbox"
                          checked={formData.wcUpdatesOptIn}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              wcUpdatesOptIn: e.target.checked,
                            }))
                          }
                          disabled={loading}
                          className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-white">
                            Send me leaderboard and World Cup Blitz updates
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-slate-400">
                            {EMAIL_UPDATES_CONSENT_TEXT}
                          </span>
                        </span>
                      </label>

                      <label className="flex gap-3 rounded-lg border border-slate-700/70 bg-slate-900/35 p-3 text-left">
                        <input
                          type="checkbox"
                          checked={formData.smsOptIn}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              smsOptIn: e.target.checked,
                            }))
                          }
                          disabled={loading}
                          className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-white">
                            Text me major bracket alerts
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-slate-400">
                            {SMS_CONSENT_TEXT}
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <label className="flex gap-3 rounded-lg border border-slate-700/70 bg-slate-900/35 p-3 text-left">
                    <input
                      type="checkbox"
                      checked={formData.ageConfirmed}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          ageConfirmed: e.target.checked,
                        }))
                      }
                      disabled={loading}
                      className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-white">
                        I am at least 13 and agree to the terms and applicable rules
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-400">
                        {ACCOUNT_TERMS_CONSENT_TEXT}
                        {isWCSignup && (
                          <>
                            {' '}The Sam&apos;s Army challenge is free to enter and subject to the{' '}
                            <Link
                              to="/official-rules"
                              className="font-bold text-emerald-300 hover:text-emerald-200"
                            >
                              Official Rules
                            </Link>
                            .
                          </>
                        )}
                      </span>
                      {errors.ageConfirmed && (
                        <span className="mt-1 block text-xs text-red-300">
                          {errors.ageConfirmed}
                        </span>
                      )}
                    </span>
                  </label>

                  {/* General error panel — themed, prominent, with support reference */}
                  {errors.general && (
                    <div className="relative rounded-xl border border-red-500/30 bg-gradient-to-r from-red-500/10 via-red-500/5 to-orange-500/10 p-4 backdrop-blur-sm">
                      <button
                        type="button"
                        aria-label="Dismiss signup issue"
                        onClick={() => {
                          setErrors(prev => ({ ...prev, general: '' }));
                          setErrorRef(null);
                        }}
                        className="absolute top-2 right-2 text-red-400/60 hover:text-red-300 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <AlertTriangle className="h-5 w-5 text-red-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-red-300">Signup Issue</p>
                          <p className="text-sm text-red-200/80 mt-1">{errors.general}</p>
                          {errorRef && (
                            <p className="text-[11px] text-red-300/40 mt-2 font-mono select-all">
                              Reference: {errorRef}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit button */}
                  <Button type="submit" className="w-full" size="lg" loading={loading}>
                    Create Account
                  </Button>
                </form>
              </>
            )}
          </CardBody>

          <CardFooter>
            <p className="text-sm text-text-secondary text-center w-full">
              Already have an account?{' '}
              <Link
                to={loginLink}
                className="text-primary-400 hover:text-primary-300 transition-colors font-medium"
              >
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>

        <AuthLegalLinks
          backLabel={isWCSignup ? 'Back to World Cup Blitz' : 'Back to home'}
          backTo={isWCSignup ? '/wc' : '/'}
          includesContestRules={isWCSignup}
        />
      </div>
    </main>
  );
};

export { RegisterPage };
